/**
 * CKYC 2.0 — Search by KIN / PAN / Mobile, Download record, Upload-to-CERSAI
 * (Phase 2 gated). Aadhaar always rendered masked.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Search, Download, Upload, ShieldAlert, Loader2, Lock } from "lucide-react";
import ProviderBadge from "../components/ProviderBadge";
import { validateImage } from "../utils/fileValidation";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function CKYCPage() {
  const [config, setConfig] = useState(null);
  const [queryType, setQueryType] = useState("pan");
  const [query, setQuery] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);
  const [purpose, setPurpose] = useState("kyc_onboarding");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);

  useEffect(() => {
    axios.get(`${API}/ckyc/config`, { withCredentials: true })
      .then(r => setConfig(r.data))
      .catch(() => setConfig({ mode: "mock", label: "CKYC v2.0 · MOCK", cersai_upload_enabled: false }));
  }, []);

  const captureConsent = async () => {
    const res = await axios.post(`${API}/consent`, {
      subject_ref: query.slice(0, 4) + "****",
      purpose,
      method: "checkbox",
      modules: ["ckyc_search", "ckyc_download"],
      evidence: { ip_captured: true, ua_captured: true },
      ttl_days: 180,
    }, { withCredentials: true });
    return res.data.id;
  };

  const handleSearch = async () => {
    if (!query.trim()) return toast.error("Enter a query");
    if (!consentChecked) return toast.error("Please confirm explicit customer consent");
    setLoading(true); setResult(null);
    try {
      const consent_id = await captureConsent();
      const res = await axios.post(`${API}/ckyc/search`,
        { query, query_type: queryType, consent_id, purpose },
        { withCredentials: true });
      setResult({ ...res.data, consent_id });
      toast.success(`Found ${res.data.count} record(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Search failed");
    } finally { setLoading(false); }
  };

  const handleDownload = async (kin) => {
    setLoading(true);
    try {
      const consent_id = await captureConsent();
      const res = await axios.post(`${API}/ckyc/download`,
        { kin, consent_id, purpose }, { withCredentials: true });
      setResult(prev => ({ ...prev, downloaded: res.data.record }));
      toast.success("Record downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    } finally { setLoading(false); }
  };

  const handleUpload = async () => {
    if (!uploadFile) return toast.error("Choose a file");
    const err = validateImage(uploadFile);
    if (err && !uploadFile.type.includes("pdf")) return toast.error(err);
    if (uploadFile.size > 5 * 1024 * 1024) return toast.error("File too large. Max 5MB");
    setLoading(true);
    try {
      const consent_id = await captureConsent();
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("consent_id", consent_id);
      fd.append("purpose", "ckyc_filing");
      const res = await axios.post(`${API}/ckyc/upload`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Queued for CERSAI: " + res.data.file_id);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Upload disabled");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="ckyc-page" style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: "#f1f5f9", margin: 0 }}>
            CKYC 2.0 · CERSAI
          </h1>
          <p style={{ color: "#64748b", marginTop: 6, fontSize: 13 }}>
            Search the central KYC registry. Aadhaar is always masked (last 4 only).
          </p>
        </div>
        {config && (
          <ProviderBadge mode={config.mode} label={config.label} slug="ckyc" />
        )}
      </div>

      {/* Search */}
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 20, marginBottom: 24 }}>
        <h3 style={{ color: "#f1f5f9", fontSize: 14, fontWeight: 700, marginTop: 0 }}>Search</h3>
        <div className="grid grid-cols-3 gap-3" style={{ marginBottom: 12 }}>
          {[
            { v: "kin", label: "KIN (14 digit)" },
            { v: "pan", label: "PAN (10 char)" },
            { v: "mobile", label: "Mobile (10 digit)" },
          ].map(o => (
            <button key={o.v} data-testid={`ckyc-querytype-${o.v}`}
              onClick={() => setQueryType(o.v)}
              style={{
                padding: "8px 12px", borderRadius: 8,
                background: queryType === o.v ? "#1e3a5f" : "transparent",
                border: `1px solid ${queryType === o.v ? "#2563eb" : "#1e2530"}`,
                color: queryType === o.v ? "#f1f5f9" : "#94a3b8",
                fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}>{o.label}</button>
          ))}
        </div>
        <input
          data-testid="ckyc-query-input"
          value={query} onChange={e => setQuery(e.target.value.toUpperCase())}
          placeholder={queryType === "pan" ? "ABCDE1234F" : queryType === "kin" ? "K1234567890123" : "9876543210"}
          style={{
            width: "100%", padding: "10px 12px", borderRadius: 8,
            background: "#080c12", border: "1px solid #1e2530", color: "#f1f5f9",
            fontSize: 14, fontFamily: "ui-monospace, monospace",
          }} />
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, color: "#cbd5e1", fontSize: 12 }}>
          <input type="checkbox" data-testid="ckyc-consent-checkbox"
                 checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} />
          Customer has explicitly consented to a CKYC look-up for purpose: <strong>{purpose}</strong>.
        </label>
        <button onClick={handleSearch} disabled={loading} data-testid="ckyc-search-btn"
          style={{
            marginTop: 14, padding: "10px 22px", borderRadius: 8,
            background: "#2563eb", color: "#fff", border: "none",
            fontSize: 13, fontWeight: 700, cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 8,
          }}>
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Search
        </button>
      </div>

      {/* Results */}
      {result?.results?.map((r, i) => (
        <div key={i} data-testid={`ckyc-result-${i}`}
             style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 18, marginBottom: 14 }}>
          <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
            <div style={{ color: "#f1f5f9", fontWeight: 700 }}>{r.full_name}</div>
            <button onClick={() => handleDownload(r.kin)} data-testid={`ckyc-download-${i}`}
              style={{ background: "#10b981", color: "#0a0d12", padding: "5px 12px",
                       borderRadius: 6, fontSize: 11, fontWeight: 700, border: "none",
                       cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Download size={11} /> Download Full Record
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3" style={{ fontSize: 12, color: "#cbd5e1" }}>
            <div><span style={{ color: "#64748b" }}>KIN:</span> {r.kin}</div>
            <div><span style={{ color: "#64748b" }}>DOB:</span> {r.dob}</div>
            <div><span style={{ color: "#64748b" }}>Aadhaar:</span> <code style={{ color: "#22d3ee" }}>{r.aadhaar_masked}</code></div>
            <div><span style={{ color: "#64748b" }}>PAN:</span> <code>{r.pan_masked}</code></div>
            <div><span style={{ color: "#64748b" }}>Mobile:</span> {r.mobile_masked}</div>
            <div><span style={{ color: "#64748b" }}>Status:</span> {r.kyc_status}</div>
          </div>
          <div style={{ marginTop: 10, fontSize: 10, color: "#475569" }}>{r._source}</div>
        </div>
      ))}

      {/* Upload to CERSAI — Phase 2 gated */}
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 20 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
          <h3 style={{ color: "#f1f5f9", fontSize: 14, fontWeight: 700, margin: 0 }}>
            Upload to CERSAI
          </h3>
          <span data-testid="ckyc-upload-phase-badge" style={{
            padding: "3px 10px", borderRadius: 999,
            background: config?.cersai_upload_enabled ? "rgba(16,185,129,0.12)" : "rgba(245,158,11,0.12)",
            border: `1px solid ${config?.cersai_upload_enabled ? "#10b98144" : "#f59e0b44"}`,
            color: config?.cersai_upload_enabled ? "#10b981" : "#f59e0b",
            fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6,
          }}>
            {config?.cersai_upload_enabled ? <>Available</> : <><Lock size={9} /> Phase 2</>}
          </span>
        </div>
        <p style={{ color: "#64748b", fontSize: 12, marginTop: 0 }}>
          {config?.cersai_upload_phase || "Loading…"}
        </p>
        <input type="file" data-testid="ckyc-upload-input"
               accept="image/jpeg,image/png,application/pdf"
               onChange={e => setUploadFile(e.target.files?.[0] || null)}
               style={{ color: "#94a3b8", fontSize: 12 }} />
        <div>
          <button onClick={handleUpload} disabled={loading} data-testid="ckyc-upload-btn"
            style={{
              marginTop: 12, padding: "8px 18px", borderRadius: 8,
              background: config?.cersai_upload_enabled ? "#10b981" : "#334155",
              color: config?.cersai_upload_enabled ? "#0a0d12" : "#94a3b8",
              border: "none", fontSize: 12, fontWeight: 700,
              cursor: config?.cersai_upload_enabled ? "pointer" : "not-allowed",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}>
            <Upload size={12} /> Upload to CERSAI
          </button>
        </div>
      </div>
    </div>
  );
}
